import { ref } from "vue";
import type { Ref } from "vue";
import parseJwt from "../utility/ParseJWT";
import { defineStore } from "pinia";
import { useCookies } from "@vueuse/integrations/useCookies";
import { addMinutes } from "date-fns";
import type {AuthInfo, ParsedAuthInfo, JWT_Token} from "../interfaces/AuthInterface";


export const TOKEN_NAMES: (keyof AuthInfo)[] = [
  "elwa_jwt",
  "tabl_jwt",
  "powerbi_jwt"
];

const REFRESH_COOKIE = "evolve24-auth";
const useAuthStore = defineStore("auth", () => {
  const cookies = useCookies();
  const authInfo: Ref<AuthInfo> = ref(newAuthData<AuthInfo>());
  const parsedAuthInfo: Ref<ParsedAuthInfo> = ref(
    newAuthData<ParsedAuthInfo>()
  );
  const refreshCodeVal = cookies.get(REFRESH_COOKIE) || "";
  const refreshCode: Ref<string> = ref(refreshCodeVal);

  function updateRefreshCookie() {
    const expires = addMinutes(new Date(), 6);
    cookies.set(REFRESH_COOKIE, refreshCode.value, { expires, secure: true, path: '/' });
  }

  function clearRefreshCookie() {
    refreshCode.value = "";
    cookies.set(REFRESH_COOKIE, "", { expires: new Date(-1), secure: true, path: '/' });
  }

  function setRefreshCode(rc: string) {
    refreshCode.value = rc;
    updateRefreshCookie();
  }
  function setAuthInfo(info: AuthInfo) {
    authInfo.value = info;
    TOKEN_NAMES.forEach((key) => {
      if (typeof info[key] === "string") {
        const jwtString = info[key] as string;
        parsedAuthInfo.value[key] = parseJwt(jwtString);
      }
    });
  }

  function $reset() {
    authInfo.value = newAuthData<AuthInfo>();
    parsedAuthInfo.value = newAuthData<ParsedAuthInfo>();
    clearRefreshCookie();
  }

  function newAuthData<T>() {
    return Object.assign(
      {},
      {
        elwa_jwt: null,
        tabl_jwt: null,
        powerbi_jwt: null,
      }
    );
  }

  function tokensAreValid() {
    let tokensAreValid = true;
    if (!parsedAuthInfo.value['elwa_jwt']) return false;
    TOKEN_NAMES.forEach((tokenName) => {
      const jwt = parsedAuthInfo.value[tokenName] as JWT_Token;
      if(jwt) {
        if(typeof jwt.exp === "undefined" || jwt.exp < (new Date().getTime() + 1) / 1000) {
          tokensAreValid = false;
        }
      }
    });
    return tokensAreValid;
  }

  return {
    authInfo,
    setAuthInfo,
    setRefreshCode,
    parsedAuthInfo,
    tokensAreValid,
    $reset,
    refreshCode,
    updateRefreshCookie,
  };
});

export default useAuthStore;
